/**
 * InvestigationTab component
 *
 * Displays forensic context from trace metadata, root cause analysis
 * and risk assessment from the generate_incident_report action, and
 * similar traces from the search_similar_prompts action.
 */

import { useCallback, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Link2, ChevronRight } from "lucide-react";
import type { SecurityIssue } from "@/types/security";
import type { SimilarTrace } from "@/components/security/SimilarTracesDialog";
import type { IncidentReport } from "@/components/security/IncidentReportPanel";
import {
  useLatestActionResult,
  useExecuteAction,
} from "@/hooks/useIssueActions";
import { useLinkTrace } from "@/hooks/useLinkTrace";
import { SEVERITY_DOT, SEVERITY_BADGE } from "@/constants/severity";

interface InvestigationTabProps {
  issue: SecurityIssue;
  numericIssueId: number;
}

export function InvestigationTab({
  issue,
  numericIssueId,
}: InvestigationTabProps) {
  const { data: reportAction, isLoading: reportLoading } =
    useLatestActionResult(numericIssueId, "generate_incident_report");

  const { data: tracesAction, isLoading: tracesLoading } =
    useLatestActionResult(numericIssueId, "search_similar_prompts");

  const executeAction = useExecuteAction();
  const linkTrace = useLinkTrace();

  const reportResult = reportAction?.result as { report?: IncidentReport } | null;
  const incidentReport = reportResult?.report ?? null;
  const narrative = incidentReport?.ai_narrative ?? null;
  const fallbackReason = incidentReport?.fallback_reason ?? null;

  const tracesResult = tracesAction?.result as {
    traces?: SimilarTrace[];
    total_found?: number;
  } | null;
  const similarTraces = tracesResult?.traces ?? null;
  const totalFound = tracesResult?.total_found ?? 0;

  const handleGenerateReport = useCallback(() => {
    executeAction.mutate({
      issueId: numericIssueId,
      data: {
        action_type: "generate_incident_report",
        action_category: "investigation",
      },
    });
  }, [executeAction, numericIssueId]);

  const handleSearchTraces = useCallback(() => {
    executeAction.mutate({
      issueId: numericIssueId,
      data: {
        action_type: "search_similar_prompts",
        action_category: "investigation",
      },
    });
  }, [executeAction, numericIssueId]);

  const handleLink = useCallback(
    (traceId: number) => {
      linkTrace.mutate({ issueId: numericIssueId, traceId });
    },
    [numericIssueId, linkTrace],
  );

  const isGeneratingReport =
    executeAction.isPending &&
    executeAction.variables?.data.action_type === "generate_incident_report";

  const isSearchingTraces =
    executeAction.isPending &&
    executeAction.variables?.data.action_type === "search_similar_prompts";

  const [expandedTraces, setExpandedTraces] = useState<Set<number>>(new Set());

  const toggleTrace = useCallback((traceId: number) => {
    setExpandedTraces((prev) => {
      const next = new Set(prev);
      if (next.has(traceId)) {
        next.delete(traceId);
      } else {
        next.add(traceId);
      }
      return next;
    });
  }, []);

  const forensicFields: { label: string; value: string; mono?: boolean }[] = [
    { label: "Trace ID", value: issue.traceId, mono: true },
    { label: "Detected", value: new Date(issue.timestamp).toLocaleString() },
    ...(issue.metadata.endpoint
      ? [{ label: "Endpoint", value: issue.metadata.endpoint as string, mono: true }]
      : []),
    ...(issue.metadata.model
      ? [{ label: "Model", value: issue.metadata.model as string, mono: true }]
      : []),
    ...(issue.metadata.userId
      ? [{ label: "User ID", value: issue.metadata.userId as string, mono: true }]
      : []),
  ];

  return (
    <div className="mt-6 space-y-5">
      {/* Forensic Context */}
      <section className="rounded-lg border bg-card p-5">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-4">
          Forensic Context
        </h3>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
          {forensicFields.map((field) => (
            <div key={field.label}>
              <dt className="text-xs text-muted-foreground">{field.label}</dt>
              <dd className={`text-sm mt-0.5 ${field.mono ? "font-mono text-xs" : ""}`}>
                {field.value}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Root Cause Analysis */}
      <section className="rounded-lg border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Root Cause Analysis
          </h3>
          {narrative && (
            <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">
              Auto-generated
            </span>
          )}
        </div>

        {reportLoading || isGeneratingReport ? (
          <div className="flex items-center justify-center py-10 gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {isGeneratingReport ? "Generating analysis..." : "Loading..."}
            </span>
          </div>
        ) : narrative ? (
          <div className="space-y-4">
            {narrative.root_cause_hypothesis && (
              <div className="border-l-2 border-muted-foreground/20 pl-4">
                <h4 className="text-xs font-medium text-muted-foreground mb-1">
                  Root Cause Hypothesis
                </h4>
                <p className="text-sm leading-relaxed">
                  {narrative.root_cause_hypothesis}
                </p>
              </div>
            )}

            {narrative.timeline_narrative && (
              <div className="border-l-2 border-muted-foreground/20 pl-4">
                <h4 className="text-xs font-medium text-muted-foreground mb-1">
                  Timeline
                </h4>
                <p className="text-sm leading-relaxed whitespace-pre-line">
                  {narrative.timeline_narrative}
                </p>
              </div>
            )}

            {narrative.executive_summary && (
              <div className="border-l-2 border-muted-foreground/20 pl-4">
                <h4 className="text-xs font-medium text-muted-foreground mb-1">
                  Executive Summary
                </h4>
                <p className="text-sm leading-relaxed">
                  {narrative.executive_summary}
                </p>
              </div>
            )}
          </div>
        ) : fallbackReason ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground mb-2">
              {fallbackReason}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => executeAction.mutate({
                issueId: numericIssueId,
                data: { action_type: "generate_incident_report", action_category: "investigation" },
                force: true,
              })}
              disabled={isGeneratingReport}
            >
              Retry Analysis
            </Button>
          </div>
        ) : (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              No analysis generated yet.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateReport}
              disabled={isGeneratingReport}
            >
              Generate Analysis
            </Button>
          </div>
        )}
      </section>

      {/* Risk Assessment */}
      {narrative?.risk_assessment && (
        <section className="rounded-lg border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Risk Assessment
            </h3>
            <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">
              Auto-generated
            </span>
          </div>
          <div className="border-l-2 border-orange-400/40 pl-4">
            <p className="text-sm leading-relaxed">{narrative.risk_assessment}</p>
          </div>
        </section>
      )}

      {/* Similar Traces */}
      <section className="rounded-lg border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Similar Traces
            {similarTraces && similarTraces.length > 0 && (
              <span className="ml-2 text-foreground/60">{totalFound}</span>
            )}
          </h3>
        </div>

        {tracesLoading || isSearchingTraces ? (
          <div className="flex items-center justify-center py-10 gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {isSearchingTraces ? "Searching..." : "Loading..."}
            </span>
          </div>
        ) : similarTraces ? (
          similarTraces.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No similar traces found.
            </p>
          ) : (
            <div className="rounded-lg border divide-y divide-border">
              {similarTraces.map((trace) => {
                const isExpanded = expandedTraces.has(trace.trace_id);
                const hasDetails =
                  trace.content_preview != null ||
                  trace.source != null ||
                  trace.model_name != null ||
                  trace.timestamp != null ||
                  trace.prompt_tokens != null ||
                  trace.completion_tokens != null ||
                  (trace.tags != null && trace.tags.length > 0);

                return (
                  <div key={trace.trace_id}>
                    {/* Summary row */}
                    <div
                      className={`px-3.5 py-2.5 flex items-center gap-3 text-sm ${
                        hasDetails ? "cursor-pointer hover:bg-muted/30 transition-colors" : ""
                      }`}
                      onClick={() => hasDetails && toggleTrace(trace.trace_id)}
                    >
                      {hasDetails && (
                        <ChevronRight
                          className={`h-3.5 w-3.5 text-muted-foreground flex-shrink-0 transition-transform ${
                            isExpanded ? "rotate-90" : ""
                          }`}
                        />
                      )}
                      <span
                        className={`h-2 w-2 rounded-full flex-shrink-0 ${
                          SEVERITY_DOT[trace.severity] ?? "bg-gray-400"
                        }`}
                      />
                      <span className="font-mono text-xs text-muted-foreground flex-shrink-0">
                        #{trace.trace_id}
                      </span>
                      <span className="text-xs truncate">{trace.event_type}</span>
                      <Badge
                        className={`text-[10px] font-medium uppercase px-1.5 py-0 border ml-auto flex-shrink-0 ${
                          SEVERITY_BADGE[trace.severity] ?? SEVERITY_BADGE.info
                        }`}
                      >
                        {trace.severity}
                      </Badge>
                      {trace.anomaly_score > 0 && (
                        <span
                          className={`text-xs tabular-nums flex-shrink-0 ${
                            trace.anomaly_score >= 0.75
                              ? "text-red-600 dark:text-red-400"
                              : trace.anomaly_score >= 0.5
                                ? "text-orange-600 dark:text-orange-400"
                                : "text-muted-foreground"
                          }`}
                        >
                          {trace.anomaly_score.toFixed(2)}
                        </span>
                      )}
                      {trace.already_linked ? (
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 flex-shrink-0"
                        >
                          Linked
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs gap-1 flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLink(trace.trace_id);
                          }}
                          disabled={linkTrace.isPending}
                        >
                          {linkTrace.isPending &&
                          linkTrace.variables?.traceId === trace.trace_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Link2 className="h-3 w-3" />
                          )}
                          Link
                        </Button>
                      )}
                    </div>

                    {/* Expanded detail panel */}
                    {isExpanded && hasDetails && (
                      <div className="px-3.5 pb-3 pt-1 bg-muted/20 border-t border-dashed">
                        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 ml-6">
                          {trace.source && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">Source</dt>
                              <dd className="text-xs">{trace.source}</dd>
                            </div>
                          )}
                          {trace.model_name && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">Model</dt>
                              <dd className="text-xs font-mono">{trace.model_name}</dd>
                            </div>
                          )}
                          {trace.timestamp && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">Timestamp</dt>
                              <dd className="text-xs">
                                {new Date(trace.timestamp).toLocaleString()}
                              </dd>
                            </div>
                          )}
                          {(trace.prompt_tokens != null || trace.completion_tokens != null) && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">Tokens</dt>
                              <dd className="text-xs tabular-nums">
                                {trace.prompt_tokens ?? 0} in / {trace.completion_tokens ?? 0} out
                              </dd>
                            </div>
                          )}
                          {trace.span_name && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">Span</dt>
                              <dd className="text-xs font-mono">{trace.span_name}</dd>
                            </div>
                          )}
                          {trace.anomaly_score > 0 && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">Anomaly</dt>
                              <dd className="flex items-center gap-2">
                                <div className="h-1 w-16 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      trace.anomaly_score >= 0.75
                                        ? "bg-red-500"
                                        : trace.anomaly_score >= 0.5
                                          ? "bg-orange-500"
                                          : "bg-blue-400"
                                    }`}
                                    style={{
                                      width: `${Math.min(trace.anomaly_score * 100, 100)}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-xs tabular-nums">
                                  {trace.anomaly_score.toFixed(2)}
                                </span>
                              </dd>
                            </div>
                          )}
                        </dl>
                        {trace.content_preview && (
                          <div className="mt-2 ml-6 rounded bg-muted/50 p-2 text-xs text-muted-foreground font-mono whitespace-pre-wrap break-words line-clamp-4">
                            {trace.content_preview}
                          </div>
                        )}
                        {trace.tags && trace.tags.length > 0 && (
                          <div className="mt-2 ml-6 flex flex-wrap gap-1">
                            {trace.tags.map((tag) => (
                              <Badge
                                key={tag}
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0"
                              >
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              No trace search performed yet.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSearchTraces}
              disabled={isSearchingTraces}
            >
              Search Similar Traces
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
